import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, RefreshCw, Save, X, Volume2, CheckCircle2, AlertCircle,
  Clock, Search, Mic, Sparkles, ChevronDown, ChevronUp, Loader2, Wand2, FolderTree, GraduationCap, Timer, Workflow, Play, Film, Smartphone, Dumbbell,
} from "lucide-react";
import { toast } from "sonner";
import { useRoninCueAdmin } from "@/pages/admin/useRoninCueAdmin";
import CircuitExercisesPanel from "@/pages/admin/CircuitExercisesPanel";

const CATEGORIES = ["punch", "defense", "footwork", "feint", "atmosphere"];
const MODES = ["shadow", "bag", "defense"];
// Ratio engine config — these are the categories the sequencer cycles through.
// Range/atmosphere are intentionally excluded (range has its own per-round scheduling,
// atmosphere is a probabilistic pre-empt driven by atmosphere_chance).
const RATIO_CATEGORIES = ["punch", "feint", "footwork", "defense"];
const RATIO_MODES = ["shadow", "bag", "defense", "custom"];
const CAT_COLOR = {
  punch: "#FF331F", defense: "#3b82f6", footwork: "#8b5cf6", feint: "#f59e0b", atmosphere: "#10b981",
};

const blank = { text: "", tts_text: "", category: "punch", punches: 1, movement_count: 0, cue_seconds: "", weight: 1, modes: ["shadow", "bag", "defense"], levels: ["beginner", "intermediate", "advanced"], active: true };

export default function RoninBoxAdmin() {
  const {
    cues, setCues, settings, setSettings, voices, loading, q, setQ, filterCat, setFilterCat,
    editing, setEditing, bulkRunning, bulkProgress, showSettings, setShowSettings,
    suggestOpen, setSuggestOpen, suggestions, setSuggestions, suggestLoading,
    selectedSuggestionIds, setSelectedSuggestionIds, applying,
    openSuggest, toggleSuggestion, applySuggestions, load, loadVoices, filtered, stats,
    regen, genMissing, regenAll, regenNumbered, spellOutNumbered, autofillMovement,
    tagNumberedEverywhere, remove, seedDefaults,
  } = useRoninCueAdmin();

  // Dynamic taxonomy so EVERY category (jumprope, etc.) + mode is manageable here in one place.
  const [cats, setCats] = useState([]);
  const [modeList, setModeList] = useState([]);
  useEffect(() => {
    api.get("/admin/ronin-box/categories").then(({ data }) => setCats(data || [])).catch(() => {});
    api.get("/admin/ronin-box/modes").then(({ data }) => setModeList(data || [])).catch(() => {});
  }, []);
  const catIds = cats.length ? cats.map((c) => c.id) : CATEGORIES;
  const catColor = (id) => cats.find((c) => c.id === id)?.color || CAT_COLOR[id] || "#71717A";

  if (loading || !settings) return <div className="p-10 text-sm text-zinc-500">Loading…</div>;


  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-ronin-box-page">
      {cues.length === 0 && (
        <div className="border border-amber-700/40 bg-amber-900/10 rounded-sm p-5 flex items-start gap-3" data-testid="ronin-empty-state">
          <Sparkles className="h-5 w-5 text-amber-300 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-amber-200 font-bold uppercase tracking-widest text-xs mb-1">Cue library is empty</p>
            <p className="text-sm text-zinc-300 mb-3">Looks like the default cues didn&apos;t auto-seed on this environment (common after a fresh deploy). One click loads the 70 stock phrases.</p>
            <Button onClick={seedDefaults} className="bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-widest text-xs" data-testid="ronin-seed-now">
              <Sparkles className="h-4 w-4 mr-2" /> Seed 70 default cues
            </Button>
          </div>
        </div>
      )}
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Ronin Box</p>
          <h1 className="font-display text-3xl text-white uppercase tracking-tight">Cue Library + Voice</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Voice: <span className="text-white font-bold">{settings.voice_name || "—"}</span> · {stats.ready}/{stats.total} cues have audio
            {stats.failed > 0 && <span className="text-red-400 ml-2">· {stats.failed} failed</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/admin/ronin-box/preview" data-testid="ronin-admin-preview-link">
            <Button className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs" title="Preview the live Ronin Box experience inside admin — no need to switch tabs after editing cues/sequences/timing.">
              <Play className="h-4 w-4 mr-2" /> Preview Ronin
            </Button>
          </Link>
          <Link to="/admin/ronin-box/levels" data-testid="ronin-admin-levels-link">
            <Button variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs">
              <GraduationCap className="h-4 w-4 mr-2" /> Level assignments
            </Button>
          </Link>
          <Link to="/admin/ronin-box/timing" data-testid="ronin-admin-timing-link">
            <Button variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs">
              <Timer className="h-4 w-4 mr-2" /> Bulk timing
            </Button>
          </Link>
          <Link to="/admin/ronin-box/sequences" data-testid="ronin-admin-sequences-link">
            <Button variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs">
              <Workflow className="h-4 w-4 mr-2" /> Sequences
            </Button>
          </Link>
          <Link to="/admin/ronin-box/animations" data-testid="ronin-admin-animations-link">
            <Button variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs">
              <Film className="h-4 w-4 mr-2" /> Cue Animations
            </Button>
          </Link>
          <Link to="/admin/ronin-box/app-promo" data-testid="ronin-admin-app-promo-link">
            <Button variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs">
              <Smartphone className="h-4 w-4 mr-2" /> App Store Links
            </Button>
          </Link>
          <Link to="/admin/ronin-box/exercises" data-testid="ronin-admin-exercises-link">
            <Button variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs">
              <Dumbbell className="h-4 w-4 mr-2" /> Circuit Exercises
            </Button>
          </Link>
          <Link to="/admin/ronin-box/taxonomy" data-testid="ronin-admin-taxonomy-link">
            <Button variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs">
              <FolderTree className="h-4 w-4 mr-2" /> Modes & Categories
            </Button>
          </Link>
          <Button onClick={() => setShowSettings((s) => !s)} variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs" data-testid="ronin-admin-settings-btn">
            <Mic className="h-4 w-4 mr-2" /> {showSettings ? "Hide" : "Settings"}
          </Button>
          <Button onClick={openSuggest} variant="outline" className="border-amber-700/60 bg-amber-900/10 text-amber-200 hover:bg-amber-900/20 uppercase tracking-widest font-bold text-xs" data-testid="ronin-admin-suggest-tts">
            <Wand2 className="h-4 w-4 mr-2" /> Suggest TTS
          </Button>
          <Button onClick={regenNumbered} disabled={bulkRunning} variant="outline" className="border-blue-700/60 bg-blue-900/10 text-blue-200 hover:bg-blue-900/20 uppercase tracking-widest font-bold text-xs" data-testid="ronin-admin-regen-numbered" title="Regenerate audio for cues whose text contains digits (1, 1-2, 1-2-3, etc.). Fixes the silent-clip bug by spelling digits as words.">
            <Sparkles className="h-4 w-4 mr-2" /> Fix numbered cues
          </Button>
          <Button onClick={spellOutNumbered} disabled={bulkRunning} className="bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-widest text-xs" data-testid="ronin-admin-spell-out-numbered" title='BULLETPROOF FIX — rewrites the TEXT field of every numbered cue ("1-2-3" → "One-Two-Three"), clears tts_text overrides, and regenerates audio. Modes/levels/weights are untouched. Safe to re-run.'>
            <Sparkles className="h-4 w-4 mr-2" /> Spell out numbered
          </Button>
          <Button onClick={tagNumberedEverywhere} disabled={bulkRunning} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold uppercase tracking-widest text-xs" data-testid="ronin-admin-tag-numbered-everywhere" title="Tag every numbered cue (digits OR spelled-out) for ALL modes (Shadow/Bag/Defense) and ALL levels (Beg/Int/Adv). Fixes the bug where numbered cues don't play in certain modes/levels because they're missing those tags.">
            <Sparkles className="h-4 w-4 mr-2" /> Tag for all modes/levels
          </Button>
          <Button onClick={autofillMovement} disabled={bulkRunning} variant="outline" className="border-cyan-700/60 bg-cyan-900/10 text-cyan-200 hover:bg-cyan-900/20 uppercase tracking-widest font-bold text-xs" data-testid="ronin-admin-autofill-movement" title="Infer movement_count from cue text on every footwork + defense cue. 'Slip slip duck' → 3, 'Pivot left' → 1. Only touches cues currently at 0 unless you confirm force-overwrite.">
            <Sparkles className="h-4 w-4 mr-2" /> Autofill movements
          </Button>
          <Button onClick={regenAll} disabled={bulkRunning} className="bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-widest text-xs" data-testid="ronin-admin-regen-all">
            {bulkRunning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {bulkProgress?.done}/{bulkProgress?.total}</> : <><Sparkles className="h-4 w-4 mr-2" /> Regenerate all</>}
          </Button>
          <Button onClick={() => setEditing({ ...blank })} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs" data-testid="ronin-admin-new-cue">
            <Plus className="h-4 w-4 mr-2" /> New cue
          </Button>
        </div>
      </header>

      {bulkRunning && bulkProgress && (
        <div className="border border-amber-700/40 bg-amber-900/10 p-4 rounded-sm" data-testid="ronin-admin-bulk-progress">
          <div className="flex items-center justify-between mb-2 text-xs text-amber-200 uppercase tracking-widest font-bold">
            <span>Generating audio · {bulkProgress.done}/{bulkProgress.total}</span>
            <span>{bulkProgress.succeeded} ok · {bulkProgress.failed} failed</span>
          </div>
          <div className="h-2 bg-amber-900/30 rounded-sm overflow-hidden">
            <div className="h-full bg-amber-400 transition-all" style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }} />
          </div>
        </div>
      )}

      {showSettings && (
        <SettingsPanel
          settings={settings} voices={voices}
          onLoadVoices={loadVoices}
          onSaved={(s) => { setSettings(s); toast.success("Settings saved"); }}
        />
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cue text…" className="pl-9 bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ronin-admin-search" />
        </div>
        <div className="flex items-center gap-1 bg-[#0A0A0A] border border-[#27272A] rounded-sm p-1">
          <FilterChip active={filterCat === "all"} onClick={() => setFilterCat("all")} label="All" count={cues.length} />
          {catIds.map((cat) => (
            <FilterChip key={cat} active={filterCat === cat} onClick={() => setFilterCat(cat)}
              label={cat} color={catColor(cat)}
              count={cues.filter((c) => c.category === cat).length}
              testid={`ronin-filter-${cat}`} />
          ))}
        </div>

        {/* Missing-audio badge + one-tap fix */}
        {stats.missing > 0 ? (
          <div className="flex items-center gap-2 ml-auto" data-testid="ronin-missing-audio">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-amber-900/15 border border-amber-700/40 text-amber-300 text-[11px] uppercase tracking-widest font-bold" data-testid="ronin-missing-audio-badge">
              <AlertCircle className="h-3.5 w-3.5" /> {stats.missing} missing audio
            </span>
            <Button onClick={genMissing} disabled={bulkRunning} className="bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-widest text-xs" data-testid="ronin-gen-missing-audio">
              {bulkRunning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {bulkProgress?.done}/{bulkProgress?.total}</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate missing</>}
            </Button>
          </div>
        ) : cues.length > 0 ? (
          <span className="inline-flex items-center gap-1.5 ml-auto px-2.5 py-1.5 text-[11px] uppercase tracking-widest font-bold text-emerald-400" data-testid="ronin-all-audio-ready">
            <CheckCircle2 className="h-3.5 w-3.5" /> All audio ready
          </span>
        ) : null}
      </div>

      {/* Cue table */}
      <div className="border border-[#27272A] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F] border-b border-[#27272A] text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="p-3 text-left">Text</th>
              <th className="p-3 text-left">Category</th>
              <th className="p-3 text-left">Punches</th>
              <th className="p-3 text-left">Weight</th>
              <th className="p-3 text-left">Modes</th>
              <th className="p-3 text-left">Audio</th>
              <th className="p-3 text-left">Active</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="8" className="p-10 text-center text-zinc-500">No cues match your filter.</td></tr>
            ) : filtered.map((c) => (
              <CueRow key={c.id} cue={c}
                getColor={catColor}
                onEdit={() => setEditing(c)}
                onRegen={() => regen(c.id)}
                onDelete={() => remove(c)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={suggestOpen} onOpenChange={(v) => !v && !applying && setSuggestOpen(false)}>
        <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="ronin-suggest-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-amber-400" /> Suggest TTS overrides
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-400 mt-1">
            Adds &ldquo;!&rdquo; to short punches/feints/defense moves so ElevenLabs delivers them with more punch. Existing overrides are skipped. Re-bake audio afterward to hear the difference.
          </p>
          {suggestLoading ? (
            <div className="py-10 text-center text-zinc-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Scanning your cue library…</div>
          ) : !suggestions || suggestions.total === 0 ? (
            <div className="py-10 text-center text-zinc-500">No suggestions — every short cue already has a TTS override.</div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-zinc-400 my-3">
                <div>
                  <span className="text-white font-bold">{selectedSuggestionIds.size}</span> of {suggestions.total} selected
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setSelectedSuggestionIds(new Set(suggestions.suggestions.map((s) => s.id)))}
                    className="text-[10px] uppercase tracking-widest font-bold text-amber-300 hover:text-amber-200"
                    data-testid="ronin-suggest-select-all">Select all</button>
                  <button onClick={() => setSelectedSuggestionIds(new Set())}
                    className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-white"
                    data-testid="ronin-suggest-select-none">Clear</button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto border border-[#27272A] rounded-sm divide-y divide-[#27272A]">
                {suggestions.suggestions.map((s) => {
                  const checked = selectedSuggestionIds.has(s.id);
                  return (
                    <label key={s.id} className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-[#0F0F0F] ${checked ? "bg-[#FF331F]/5" : ""}`} data-testid={`ronin-suggest-row-${s.id}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleSuggestion(s.id)}
                        className="h-4 w-4 accent-[#FF331F]"
                        data-testid={`ronin-suggest-check-${s.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: CAT_COLOR[s.category] }} />
                          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{s.category}</span>
                          <span className="text-white font-bold text-sm truncate">{s.text}</span>
                        </div>
                        <div className="text-xs text-zinc-400 ml-3.5">
                          TTS: <span className="text-amber-200 font-mono">{s.suggested_tts_text}</span>
                          {s.current_tts_text && <span className="text-zinc-600 ml-2">(was: {s.current_tts_text})</span>}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
          <div className="flex justify-end gap-2 pt-3 border-t border-[#27272A] mt-3">
            <Button variant="outline" disabled={applying} onClick={() => setSuggestOpen(false)}
              className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white rounded-sm">Cancel</Button>
            <Button onClick={applySuggestions} disabled={applying || !suggestions || selectedSuggestionIds.size === 0}
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-widest text-xs rounded-sm"
              data-testid="ronin-suggest-apply">
              {applying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying…</> : <><Wand2 className="h-4 w-4 mr-2" /> Apply {selectedSuggestionIds.size} override{selectedSuggestionIds.size === 1 ? "" : "s"}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {editing && (
        <CueDialog cue={editing} cats={cats} modeList={modeList} onClose={() => setEditing(null)} onSaved={(c) => {
          if (editing.id) setCues((cs) => cs.map((x) => x.id === c.id ? c : x));
          else setCues((cs) => [c, ...cs]);
          setEditing(null);
        }} />
      )}

      {/* Circuit exercises — managed here too so every mode's cues live in one place */}
      <div className="mt-10 pt-8 border-t border-[#27272A]" data-testid="cue-library-exercises-section">
        <div className="flex items-center gap-2 mb-1">
          <Dumbbell className="h-5 w-5 text-[#FF331F]" />
          <h2 className="font-display text-xl text-white uppercase tracking-tight">Circuit Exercises</h2>
        </div>
        <p className="text-xs text-zinc-500 mb-5">Used only in <span className="text-zinc-300 font-bold">Circuit mode</span> breaks. Add, rename, toggle & generate voice — all without leaving the Cue Library.</p>
        <CircuitExercisesPanel />
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, color, testid }) {
  return (
    <button onClick={onClick} className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold rounded-sm transition-colors ${active ? "bg-[#FF331F] text-white" : "text-zinc-400 hover:text-white"}`} data-testid={testid}>
      {color && <span className="inline-block w-2 h-2 mr-1.5 rounded-full" style={{ background: color }} />}
      {label} <span className="opacity-60">{count}</span>
    </button>
  );
}

function CueRow({ cue, onEdit, onRegen, onDelete, getColor }) {
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const audioSrc = cue.audio_url ? `${apiBase}${cue.audio_url}` : null;
  const color = getColor ? getColor(cue.category) : (CAT_COLOR[cue.category] || "#71717A");
  const play = () => {
    if (!audioSrc) return;
    const a = new Audio(audioSrc);
    a.play().catch(() => toast.error("Couldn't play audio"));
  };
  return (
    <tr className="border-b border-[#27272A] hover:bg-[#0F0F0F]" data-testid={`ronin-cue-row-${cue.id}`}>
      <td className="p-3 text-white font-bold">{cue.text}</td>
      <td className="p-3">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
          {cue.category}
        </span>
      </td>
      <td className="p-3 text-zinc-400">{cue.punches > 0 ? cue.punches : "—"}</td>
      <td className="p-3 text-zinc-400">{cue.weight}</td>
      <td className="p-3 text-[10px] uppercase tracking-widest text-zinc-500">
        {(cue.modes || []).map((m) => m[0].toUpperCase()).join(" ") || "—"}
      </td>
      <td className="p-3">
        {cue.audio_status === "ready" && (
          <button onClick={play} className="inline-flex items-center gap-1 text-emerald-400 text-[10px] uppercase tracking-widest font-bold hover:text-emerald-300" data-testid={`ronin-cue-play-${cue.id}`}>
            <Volume2 className="h-3 w-3" /> Play
          </button>
        )}
        {cue.audio_status === "pending" && (
          <span className="inline-flex items-center gap-1 text-zinc-500 text-[10px] uppercase tracking-widest font-bold"><Clock className="h-3 w-3" /> Pending</span>
        )}
        {cue.audio_status === "failed" && (
          <span title={cue.audio_error || ""} className="inline-flex items-center gap-1 text-red-400 text-[10px] uppercase tracking-widest font-bold"><AlertCircle className="h-3 w-3" /> Failed</span>
        )}
        {cue.audio_status === "regenerating" && (
          <span className="inline-flex items-center gap-1 text-amber-400 text-[10px] uppercase tracking-widest font-bold"><Loader2 className="h-3 w-3 animate-spin" /> Generating</span>
        )}
      </td>
      <td className="p-3">
        {cue.active ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <X className="h-4 w-4 text-zinc-600" />}
      </td>
      <td className="p-3 text-right">
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={onRegen} className="text-zinc-400 hover:text-amber-300 h-8 px-2" data-testid={`ronin-cue-regen-${cue.id}`}><RefreshCw className="h-3 w-3" /></Button>
          <Button size="sm" variant="ghost" onClick={onEdit} className="text-zinc-400 hover:text-white h-8 px-2" data-testid={`ronin-cue-edit-${cue.id}`}><Pencil className="h-3 w-3" /></Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-zinc-400 hover:text-red-400 h-8 px-2" data-testid={`ronin-cue-delete-${cue.id}`}><Trash2 className="h-3 w-3" /></Button>
        </div>
      </td>
    </tr>
  );
}

function CueDialog({ cue, onClose, onSaved, cats = [], modeList = [] }) {
  const isNew = !cue.id;
  const catOptions = cats.length ? cats.map((c) => c.id) : CATEGORIES;
  const modeOptions = modeList.length ? modeList.map((m) => m.id) : MODES;
  // Hydrate the form: spread existing cue fields, but normalize cue_seconds for the input
  // (null/undefined → "" so the controlled <input type=number> stays empty until the admin types).
  const [form, setForm] = useState(() => ({
    movement_count: 0,
    ...cue,
    cue_seconds: cue.cue_seconds == null ? "" : cue.cue_seconds,
  }));
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const toggleMode = (m) => {
    setForm((f) => {
      const has = (f.modes || []).includes(m);
      return { ...f, modes: has ? f.modes.filter((x) => x !== m) : [...(f.modes || []), m] };
    });
  };

  const preview = async () => {
    const text = (form.tts_text || form.text || "").trim();
    if (!text) { toast.error("Add text first"); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/ronin-box/preview", { text });
      const audio = new Audio(`${apiBase}${data.audio_url}`);
      audio.play().catch(() => toast.error("Couldn't play audio"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Preview failed");
    } finally { setPreviewing(false); }
  };
  const save = async () => {
    if (!form.text.trim()) { toast.error("Text required"); return; }
    setSaving(true);
    try {
      const payload = {
        text: form.text, tts_text: (form.tts_text || "").trim() || null,
        category: form.category, punches: Number(form.punches) || 0,
        movement_count: Math.max(0, Math.floor(Number(form.movement_count) || 0)),
        // Empty string ↔ "no override (use defaults)" — convert to null so backend
        // stores it explicitly rather than coercing to 0 and freezing the cue at 0.5s.
        cue_seconds: (form.cue_seconds === "" || form.cue_seconds === null || form.cue_seconds === undefined) ? null : Math.max(0.5, Math.min(15, Number(form.cue_seconds))),
        weight: Math.max(1, Number(form.weight) || 1), modes: form.modes || [],
        levels: (form.levels && form.levels.length > 0) ? form.levels : ["beginner"],
        active: !!form.active,
      };
      const { data } = isNew
        ? await api.post("/admin/ronin-box/cues", payload)
        : await api.patch(`/admin/ronin-box/cues/${cue.id}`, payload);
      onSaved(data);
      toast.success(isNew ? "Cue created — click ⟳ to generate audio" : "Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-lg">
        <DialogHeader><DialogTitle className="text-white uppercase tracking-tight">{isNew ? "New cue" : `Edit · ${cue.text}`}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Text (what coach says)</Label>
            <Input value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder='e.g. "Stick him with the jab"'
              className="bg-[#050505] border-[#27272A] text-white rounded-sm"
              data-testid="ronin-cue-form-text" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">TTS override (optional)</Label>
              <button type="button" onClick={preview} disabled={previewing}
                className="text-[10px] uppercase tracking-widest font-bold text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="ronin-cue-form-preview">
                {previewing ? <><Loader2 className="h-3 w-3 animate-spin" /> Generating…</> : <><Volume2 className="h-3 w-3" /> Preview</>}
              </button>
            </div>
            <Input value={form.tts_text || ""} onChange={(e) => setForm({ ...form, tts_text: e.target.value })}
              placeholder={`Defaults to "${form.text || "(text above)"}" — punch up short words with "!" e.g. "Jab!"`}
              className="bg-[#050505] border-[#27272A] text-white rounded-sm"
              data-testid="ronin-cue-form-tts-text" />
            <p className="text-[10px] text-zinc-600 mt-1">What ElevenLabs hears. Display stays as &ldquo;Text&rdquo; above. Use punctuation (!, ., …) to shape delivery — short single-word cues sound flat without it.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ronin-cue-form-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                  {catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Levels</Label>
              <div className="flex gap-1 mt-1">
                {["beginner", "intermediate", "advanced"].map((lv) => {
                  const cur = form.levels || ["beginner"];
                  const active = cur.includes(lv);
                  return (
                    <button
                      key={lv}
                      type="button"
                      onClick={() => {
                        const next = active ? cur.filter((l) => l !== lv) : [...cur, lv];
                        // Don't allow empty (a cue must belong to at least one level)
                        if (next.length === 0) return;
                        setForm({ ...form, levels: next });
                      }}
                      className={`flex-1 px-2 py-2 text-[10px] uppercase tracking-widest font-bold border rounded-sm transition-colors ${active ? (lv === "beginner" ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" : lv === "intermediate" ? "border-amber-500/50 bg-amber-500/10 text-amber-400" : "border-red-500/50 bg-red-500/10 text-red-400") : "border-[#27272A] bg-[#050505] text-zinc-500 hover:text-white"}`}
                      data-testid={`ronin-cue-form-level-${lv}`}
                    >
                      {lv === "beginner" ? "BEG" : lv === "intermediate" ? "INT" : "ADV"}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-zinc-600 mt-1">Pick any combination.</p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Weight</Label>
              <Input type="number" min="1" max="20" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })}
                className="bg-[#050505] border-[#27272A] text-white rounded-sm"
                data-testid="ronin-cue-form-weight" />
              <p className="text-[10px] text-zinc-600 mt-1">Higher = more often</p>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Timing controls</Label>
            <div className="grid grid-cols-3 gap-3 mt-1">
              <div>
                <Input type="number" min="0" max="10" value={form.punches} onChange={(e) => setForm({ ...form, punches: e.target.value })}
                  className="bg-[#050505] border-[#27272A] text-white rounded-sm"
                  data-testid="ronin-cue-form-punches" placeholder="Punches" />
                <p className="text-[10px] text-zinc-600 mt-1">Punches</p>
              </div>
              <div>
                <Input type="number" min="0" max="10" value={form.movement_count ?? 0} onChange={(e) => setForm({ ...form, movement_count: e.target.value })}
                  className="bg-[#050505] border-[#27272A] text-white rounded-sm"
                  data-testid="ronin-cue-form-movement" placeholder="Steps" />
                <p className="text-[10px] text-zinc-600 mt-1">Movements</p>
              </div>
              <div>
                <Input type="number" min="0.5" max="15" step="0.1" value={form.cue_seconds ?? ""} onChange={(e) => setForm({ ...form, cue_seconds: e.target.value })}
                  className="bg-[#050505] border-[#27272A] text-white rounded-sm"
                  data-testid="ronin-cue-form-seconds" placeholder="Auto" />
                <p className="text-[10px] text-zinc-600 mt-1">Secs · override</p>
              </div>
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">
              <span className="text-zinc-400">Punches</span> feed the punch counter AND default the time-to-complete (1→1.7s, 2→2.4s, 3→3.0s, 4→3.6s, 5→4.0s).
              <span className="text-zinc-400"> Movements</span> default time for footwork/defense (1→1.6s, 2→2.3s, 3→2.9s, 4→3.4s).
              <span className="text-zinc-400"> Secs override</span> = absolute time at medium intensity (still scaled by intensity tier). Leave blank for auto.
            </p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2 block">Modes</Label>
            <div className="grid grid-cols-3 gap-2">
              {modeOptions.map((m) => {
                const on = (form.modes || []).includes(m);
                return (
                  <button key={m} type="button" onClick={() => toggleMode(m)}
                    className={`py-2 border rounded-sm text-xs uppercase tracking-widest font-bold transition-colors ${on ? "border-[#FF331F] bg-[#FF331F]/10 text-white" : "border-[#27272A] bg-[#050505] text-zinc-500 hover:text-white"}`}
                    data-testid={`ronin-cue-form-mode-${m}`}
                  >{m}</button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center justify-between border border-[#27272A] bg-[#050505] p-3 rounded-sm">
            <div>
              <Label className="text-white text-sm">Active</Label>
              <p className="text-[10px] text-zinc-500 mt-0.5">Inactive cues won&apos;t fire in workouts</p>
            </div>
            <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="ronin-cue-form-active" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#27272A]">
            <Button variant="outline" onClick={onClose} className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white rounded-sm"><X className="h-4 w-4 mr-1" /> Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs rounded-sm" data-testid="ronin-cue-form-save">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettingsPanel({ settings, voices, onLoadVoices, onSaved }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [voicesOpen, setVoicesOpen] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        voice_id: form.voice_id, atmosphere_chance: Number(form.atmosphere_chance),
        intensities: form.intensities,
        mode_category_weights: form.mode_category_weights,
        mode_category_ratios: form.mode_category_ratios,
        mode_range_per_round: form.mode_range_per_round,
        voice_settings: {
          stability: Number(form.voice_settings?.stability ?? 0.35),
          similarity_boost: Number(form.voice_settings?.similarity_boost ?? 0.85),
          style: Number(form.voice_settings?.style ?? 0.55),
          use_speaker_boost: form.voice_settings?.use_speaker_boost !== false,
        },
      };
      const { data } = await api.patch("/admin/ronin-box/settings", payload);
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const setIntensity = (idx, key, val) => {
    const next = [...form.intensities];
    next[idx] = { ...next[idx], [key]: Number(val) };
    setForm({ ...form, intensities: next });
  };

  const setModeWeight = (mode, cat, val) => {
    const w = { ...(form.mode_category_weights || {}) };
    w[mode] = { ...(w[mode] || {}), [cat]: Math.max(0, Number(val) || 0) };
    setForm({ ...form, mode_category_weights: w });
  };

  // Round-rhythm ratios — drives the sequencer's per-mode cycle (4P:2F:1Fw:1D etc).
  // Clamped to [0, 10] per slot. Mode keys outside the 4 base modes get a default row
  // because they're auto-backfilled server-side.
  const setModeRatio = (mode, cat, val) => {
    const r = { ...(form.mode_category_ratios || {}) };
    r[mode] = { ...(r[mode] || {}), [cat]: Math.max(0, Math.min(10, Math.floor(Number(val) || 0))) };
    setForm({ ...form, mode_category_ratios: r });
  };

  // Range-per-round frequency per mode. Stored as the literal config string ("random" / "off" /
  // "1" / "2" / "3") — the sequencer normalizes it. Sequencer enforces these timing constraints:
  //   1 cue : fires ≥30s in, ≥30s before end
  //   2 cues: ≥60s apart, last ≥30s before end
  //   3 cues: ≥40s apart, last ≥20s before end
  const setModeRange = (mode, val) => {
    const r = { ...(form.mode_range_per_round || {}) };
    r[mode] = val;
    setForm({ ...form, mode_range_per_round: r });
  };

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] rounded-sm" data-testid="ronin-admin-settings-panel">
      {/* Voice */}
      <div className="p-5 border-b border-[#27272A]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Voice</p>
            <h3 className="font-display text-lg text-white uppercase tracking-tight">Trainer voice (ElevenLabs)</h3>
          </div>
          <Button onClick={() => { setVoicesOpen((o) => { if (!o) onLoadVoices(); return !o; }); }}
            variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white text-xs uppercase tracking-widest font-bold"
            data-testid="ronin-admin-toggle-voices">
            {voicesOpen ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />} {voicesOpen ? "Hide" : "Browse voices"}
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Voice ID</Label>
            <Input value={form.voice_id || ""} onChange={(e) => setForm({ ...form, voice_id: e.target.value })}
              className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs"
              data-testid="ronin-admin-voice-id" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Current</Label>
            <div className="bg-[#050505] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white">{settings.voice_name || "Custom"}</div>
          </div>
        </div>
        {voicesOpen && (
          <div className="mt-3 max-h-[280px] overflow-y-auto border border-[#27272A] rounded-sm">
            {voices === null ? <div className="p-4 text-zinc-500 text-sm">Loading voices…</div>
              : voices.length === 0 ? <div className="p-4 text-zinc-500 text-sm">No voices found</div>
              : voices.map((v) => (
                <div key={v.voice_id} className={`flex items-center justify-between gap-2 p-3 border-b border-[#27272A] hover:bg-[#0F0F0F] cursor-pointer ${form.voice_id === v.voice_id ? "bg-[#FF331F]/10" : ""}`}
                  onClick={() => setForm({ ...form, voice_id: v.voice_id })}
                  data-testid={`ronin-voice-${v.voice_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-bold text-sm truncate">{v.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{v.voice_id}</div>
                  </div>
                  {v.preview_url && (
                    <button onClick={(e) => { e.stopPropagation(); new Audio(v.preview_url).play(); }}
                      className="shrink-0 text-amber-300 hover:text-amber-200 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1">
                      <Volume2 className="h-3 w-3" /> Preview
                    </button>
                  )}
                </div>
              ))}
          </div>
        )}
        <p className="text-[10px] text-zinc-600 mt-2">Changing the voice requires regenerating all cues (click &ldquo;Regenerate all&rdquo; above).</p>
      </div>

      {/* Voice tuning sliders — control how the chosen voice delivers each cue */}
      <div className="p-5 border-b border-[#27272A]">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Voice tuning</p>
        <h3 className="font-display text-lg text-white uppercase tracking-tight mb-1">How George speaks</h3>
        <p className="text-xs text-zinc-500 mb-3">Lower stability + higher style = punchier short cues. Defaults work well; tweak then regenerate to hear the difference.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <SliderRow label="Stability"
            help="Lower = more dynamic / expressive (recommended ~0.35)"
            value={form.voice_settings?.stability ?? 0.35}
            onChange={(v) => setForm({ ...form, voice_settings: { ...form.voice_settings, stability: v } })}
            testid="ronin-vs-stability" />
          <SliderRow label="Similarity boost"
            help="Higher = stays closer to George's voice (recommended ~0.85)"
            value={form.voice_settings?.similarity_boost ?? 0.85}
            onChange={(v) => setForm({ ...form, voice_settings: { ...form.voice_settings, similarity_boost: v } })}
            testid="ronin-vs-similarity" />
          <SliderRow label="Style"
            help="Higher = more punch / emphasis on each phrase (recommended ~0.55)"
            value={form.voice_settings?.style ?? 0.55}
            onChange={(v) => setForm({ ...form, voice_settings: { ...form.voice_settings, style: v } })}
            testid="ronin-vs-style" />
          <div className="flex items-center justify-between border border-[#27272A] bg-[#050505] p-3 rounded-sm">
            <div>
              <Label className="text-white text-sm">Speaker boost</Label>
              <p className="text-[10px] text-zinc-500 mt-0.5">Extra clarity (recommended on)</p>
            </div>
            <Switch
              checked={form.voice_settings?.use_speaker_boost ?? true}
              onCheckedChange={(v) => setForm({ ...form, voice_settings: { ...form.voice_settings, use_speaker_boost: v } })}
              data-testid="ronin-vs-speaker-boost"
            />
          </div>
        </div>
      </div>

      {/* Pacing */}
      <div className="p-5 border-b border-[#27272A]">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Pacing</p>
        <h3 className="font-display text-lg text-white uppercase tracking-tight mb-3">Cue intervals per intensity</h3>
        <div className="space-y-2">
          {form.intensities.map((tier, idx) => (
            <div key={tier.id} className="grid grid-cols-4 gap-2 items-center">
              <div className="text-white font-bold uppercase text-sm">{tier.label}</div>
              <div>
                <Label className="text-[10px] text-zinc-500">Min ms</Label>
                <Input type="number" value={tier.min_ms} onChange={(e) => setIntensity(idx, "min_ms", e.target.value)} className="bg-[#050505] border-[#27272A] text-white rounded-sm h-9" data-testid={`ronin-intensity-${tier.id}-min`} />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500">Max ms</Label>
                <Input type="number" value={tier.max_ms} onChange={(e) => setIntensity(idx, "max_ms", e.target.value)} className="bg-[#050505] border-[#27272A] text-white rounded-sm h-9" data-testid={`ronin-intensity-${tier.id}-max`} />
              </div>
              <div className="text-[10px] text-zinc-500">≈ {((tier.min_ms + tier.max_ms) / 2 / 1000).toFixed(1)}s avg</div>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Atmosphere chance ({Math.round((form.atmosphere_chance || 0) * 100)}%)</Label>
          <Input type="range" min="0" max="0.3" step="0.01" value={form.atmosphere_chance || 0}
            onChange={(e) => setForm({ ...form, atmosphere_chance: Number(e.target.value) })}
            className="w-full" data-testid="ronin-atmosphere-chance" />
          <p className="text-[10px] text-zinc-600">% chance per cue slot of firing an atmosphere line instead of a regular cue.</p>
        </div>
      </div>

      {/* Round rhythm — per-mode ratio engine */}
      <div className="p-5 border-b border-[#27272A]">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Round rhythm</p>
        <h3 className="font-display text-lg text-white uppercase tracking-tight mb-1">Cue ratio per mode</h3>
        <p className="text-[11px] text-zinc-500 mb-3 max-w-2xl">
          Per round, the engine cycles through these category counts in random order. E.g. <span className="text-zinc-300 font-bold">4 punches : 2 feints : 1 footwork : 1 defense</span> means every 8-cue cycle hits that mix. Set a count to <span className="text-zinc-300 font-bold">0</span> to remove that category from the cycle. <br />
          <span className="text-zinc-400">Range / round</span> controls how many range cues fire per round — <span className="text-zinc-300">2 cues = ≥60s apart with ≥30s left at the last call · 3 cues = ≥40s apart with ≥20s left at the last call</span>.
        </p>
        <div className="overflow-x-auto">
          <table className="text-sm" data-testid="ronin-admin-ratios-table">
            <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500">
              <th className="p-2 text-left">Mode</th>
              {RATIO_CATEGORIES.map((c) => <th key={c} className="p-2 text-left">{c}</th>)}
              <th className="p-2 text-left">Cycle</th>
              <th className="p-2 text-left">Range / round</th>
            </tr></thead>
            <tbody>
              {RATIO_MODES.map((m) => {
                const r = form.mode_category_ratios?.[m] || {};
                const total = RATIO_CATEGORIES.reduce((s, c) => s + (Number(r[c]) || 0), 0);
                const rangeVal = form.mode_range_per_round?.[m] ?? "random";
                return (
                  <tr key={m} data-testid={`ronin-admin-ratio-row-${m}`}>
                    <td className="p-2 text-white font-bold uppercase">{m}</td>
                    {RATIO_CATEGORIES.map((c) => (
                      <td key={c} className="p-1">
                        <Input
                          type="number" min="0" max="10" step="1"
                          value={r[c] ?? 0}
                          onChange={(e) => setModeRatio(m, c, e.target.value)}
                          className="w-16 bg-[#050505] border-[#27272A] text-white rounded-sm h-9 text-center"
                          data-testid={`ronin-admin-ratio-${m}-${c}`}
                        />
                      </td>
                    ))}
                    <td className="p-2 text-[#FF331F] font-bold text-xs uppercase tracking-widest">
                      {total === 0 ? <span className="text-zinc-600">empty</span> : `${total} per cycle`}
                    </td>
                    <td className="p-1">
                      <select
                        value={String(rangeVal)}
                        onChange={(e) => setModeRange(m, e.target.value)}
                        className="bg-[#050505] border border-[#27272A] text-white rounded-sm h-9 px-2 text-xs uppercase tracking-widest font-bold cursor-pointer"
                        data-testid={`ronin-admin-range-${m}`}
                      >
                        <option value="random">Random 1–2</option>
                        <option value="off">Off</option>
                        <option value="1">Exactly 1</option>
                        <option value="2">Exactly 2</option>
                        <option value="3">Exactly 3</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-zinc-600 mt-2">
          Saved per mode. Live workouts read this immediately — no redeploy needed once you click Save.
        </p>
      </div>

      {/* Mode mixer */}
      <div className="p-5 border-b border-[#27272A]">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Combos / mode mix</p>
        <h3 className="font-display text-lg text-white uppercase tracking-tight mb-3">How often each category fires per mode</h3>
        <div className="overflow-x-auto">
          <table className="text-sm">
            <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500">
              <th className="p-2 text-left">Mode</th>
              {CATEGORIES.map((c) => <th key={c} className="p-2 text-left">{c}</th>)}
            </tr></thead>
            <tbody>
              {MODES.map((m) => (
                <tr key={m}>
                  <td className="p-2 text-white font-bold uppercase">{m}</td>
                  {CATEGORIES.map((c) => (
                    <td key={c} className="p-1">
                      <Input type="number" min="0" max="10"
                        value={form.mode_category_weights?.[m]?.[c] ?? 0}
                        onChange={(e) => setModeWeight(m, c, e.target.value)}
                        className="w-16 bg-[#050505] border-[#27272A] text-white rounded-sm h-9 text-center"
                        data-testid={`ronin-mode-weight-${m}-${c}`} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-zinc-600 mt-2">0 = never; higher = more frequent. Combine with each cue&apos;s weight.</p>
      </div>

      <div className="p-5 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs" data-testid="ronin-admin-settings-save">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}

function SliderRow({ label, help, value, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</Label>
        <span className="text-xs text-white font-mono">{Number(value).toFixed(2)}</span>
      </div>
      <Input type="range" min="0" max="1" step="0.05" value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full" data-testid={testid} />
      <p className="text-[10px] text-zinc-600 mt-1">{help}</p>
    </div>
  );
}

